import { CheckCircleOutlined, FormOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Empty, List, Spin, Tag, Tooltip, Typography } from 'antd';
import { DomainLogo } from 'components/atoms/DomainLogo';
import { SalesforceSupplierSearchResult } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';

type SalesforceLookupPaneProps = {
  salesforceSupplierSearchLoading: boolean;
  salesforceSupplierResults: SalesforceSupplierSearchResult[];
  selectedSalesforceSupplierId?: string;
  selectedRecordsMatchBySalesforceId: boolean;
  vendor?: VendorAdmin;
  handleSalesforceSelection: (selection: SalesforceSupplierSearchResult) => void;
  getSalesforceTagColor: (salesforceId?: string | null) => string;
  selectedSalesforceSupplier?: SalesforceSupplierSearchResult;
  isActionLoading: boolean;
  openRenameSalesforceAccountModal: () => void;
  paneHeaderLogoClass: string;
  paneHeaderFallbackClass: string;
};

export const SalesforceLookupPane: React.FC<SalesforceLookupPaneProps> = ({
  salesforceSupplierSearchLoading,
  salesforceSupplierResults,
  selectedSalesforceSupplierId,
  selectedRecordsMatchBySalesforceId,
  vendor,
  handleSalesforceSelection,
  getSalesforceTagColor,
  selectedSalesforceSupplier,
  isActionLoading,
  openRenameSalesforceAccountModal,
  paneHeaderLogoClass,
  paneHeaderFallbackClass
}) => (
  <Col span={12}>
    <Card
      title={
        <div className="flex items-center gap-2">
          <DomainLogo
            name="Salesforce"
            domainOrWebsite="salesforce.com"
            className={paneHeaderLogoClass}
            fallbackClassName={paneHeaderFallbackClass}
          />
          <span>Salesforce Supplier Accounts</span>
        </div>
      }
      className="h-full"
    >
      <div className="max-h-60 overflow-y-auto rounded border border-gray-200">
        {salesforceSupplierSearchLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : salesforceSupplierResults.length > 0 ? (
          <List
            dataSource={salesforceSupplierResults}
            renderItem={(supplier) => {
              const isSelected = selectedSalesforceSupplierId === supplier.Id;
              const isMatchedSalesforceRow = Boolean(
                selectedRecordsMatchBySalesforceId && isSelected && supplier.Id === vendor?.salesforce_id
              );
              const supplierDealCount = supplier.Deals_as_Primary_Vendor_Last_24_Mos_RH__c ?? 'N/A';
              return (
                <List.Item
                  className={`cursor-pointer px-4 py-2 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} border-b border-gray-100`}
                  onClick={() => handleSalesforceSelection(supplier)}
                >
                  <div className="flex w-full items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <DomainLogo
                        name={supplier.Name}
                        domainOrWebsite={supplier.Website}
                        className="h-9 w-9 rounded object-contain"
                        fallbackClassName="flex h-9 w-9 items-center justify-center rounded-md bg-blue-100 text-sm font-bold text-blue-600"
                      />
                      <div className="min-w-0">
                        <Typography.Text strong>{supplier.Name}</Typography.Text>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <Tag
                            color={getSalesforceTagColor(supplier.Id)}
                            data-sfdc-color-key={getSalesforceTagColor(supplier.Id)}
                            className="!mr-0 rounded-full text-[11px]"
                          >
                            SFDC ID: {supplier.Id}
                          </Tag>
                          {isMatchedSalesforceRow && (
                            <Tag color="success" className="!mr-0 rounded-full text-[11px]">
                              <CheckCircleOutlined className="mr-1" />
                              Matched
                            </Tag>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="w-56 shrink-0 text-xs text-gray-500">
                      <Typography.Text
                        className="block text-xs text-gray-500"
                        ellipsis={{ tooltip: `Website: ${supplier.Website || 'N/A'}` }}
                      >
                        Website: {supplier.Website || 'N/A'}
                      </Typography.Text>
                      <Typography.Text className="block text-xs text-gray-500">
                        Deal Count: {supplierDealCount}
                      </Typography.Text>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty className="my-5" image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Salesforce suppliers" />
        )}
      </div>

      <Divider />

      {selectedSalesforceSupplier ? (
        <div className="rounded border border-gray-200 bg-gray-50 p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <DomainLogo
                name={selectedSalesforceSupplier.Name}
                domainOrWebsite={selectedSalesforceSupplier.Website}
                className="h-12 w-12 rounded object-contain"
                fallbackClassName="flex h-12 w-12 items-center justify-center rounded-md bg-blue-100 text-base font-bold text-blue-600"
              />
              <Typography.Title level={5} className="!mb-0">
                {selectedSalesforceSupplier.Name}
              </Typography.Title>
            </div>
            <Tooltip title="Rename Salesforce account">
              <Button
                aria-label="Rename Salesforce account"
                type="text"
                size="small"
                icon={<FormOutlined />}
                disabled={!selectedSalesforceSupplier}
                loading={isActionLoading}
                onClick={openRenameSalesforceAccountModal}
              />
            </Tooltip>
          </div>
          <Typography.Paragraph className="!mb-1 text-sm text-gray-600">
            Account ID: {selectedSalesforceSupplier.Id}
          </Typography.Paragraph>
          <Typography.Paragraph className="!mb-1 text-sm text-gray-600" ellipsis>
            Website: {selectedSalesforceSupplier.Website || 'N/A'}
          </Typography.Paragraph>
          <Typography.Paragraph className="!mb-0 text-sm text-gray-600">
            Deals as Primary Vendor (last 24 months):{' '}
            {selectedSalesforceSupplier.Deals_as_Primary_Vendor_Last_24_Mos_RH__c ?? 'N/A'}
          </Typography.Paragraph>
        </div>
      ) : (
        <Empty
          className="my-10"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a Salesforce supplier account to review details"
        />
      )}
    </Card>
  </Col>
);
